// Connie the Compliance — generator-violations dashboard.
//
// Connie is a passive validator wired into the content generator (NOT
// a runner-style agent). She writes to `generator_violations` whenever
// the post-gen audit catches a forbidden phrase in raw AI output — the
// system either retries with a corrective prompt or deterministically
// sanitizes the phrase before disk write.
//
// This page lets the operator answer:
//   • What does the AI keep trying to say that we keep banning?
//   • Is the retry-with-feedback actually working, or are we falling
//     through to the sanitizer most of the time?
//   • Which page types are dirtiest? (homepage vs service vs hyperlocal)
//   • What did the AI try to say in the last hour?

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { connieApi, type ConniePatternRow, type ConnieRecentRow } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

export function ConnieDashboardPage() {
  const [days, setDays] = useState<30 | 90 | 365>(30);

  const q = useQuery({
    queryKey: ["connie-dashboard", days],
    queryFn: () => connieApi.dashboard({ days, recentLimit: 50 }),
  });

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-ink-900">Connie the Compliance</h1>
          <p className="text-sm text-ink-600 mt-1 max-w-2xl">
            Generator-violation log. Every row is one forbidden phrase the AI emitted on a generation attempt — auto-corrected via retry-with-feedback or deterministically sanitized before disk write.
          </p>
        </div>
        <WindowToggle value={days} onChange={setDays} />
      </div>

      {q.isLoading && <p className="text-sm text-ink-500">Loading…</p>}
      {q.error && <p className="text-sm text-red-700">Failed to load: {(q.error as Error).message}</p>}

      {q.data && (
        <>
          <TotalsRow totals={q.data.totals} />

          {q.data.totals.violations === 0 ? (
            <EmptyState days={days} />
          ) : (
            <>
              <div className="grid lg:grid-cols-3 gap-5 mt-6">
                <div className="lg:col-span-2">
                  <PatternsTable rows={q.data.byPattern} />
                </div>
                <div>
                  <PageTypeBreakdown rows={q.data.byPageType} total={q.data.totals.violations} />
                </div>
              </div>

              <div className="mt-6">
                <RecentFeed rows={q.data.recent} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function WindowToggle({ value, onChange }: { value: 30 | 90 | 365; onChange: (v: 30 | 90 | 365) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-ink-200 overflow-hidden text-xs">
      {[30, 90, 365].map((d) => (
        <button
          key={d}
          onClick={() => onChange(d as 30 | 90 | 365)}
          className={`px-3 py-1.5 ${value === d ? "bg-ink-900 text-white" : "bg-white text-ink-700 hover:bg-ink-50"}`}
        >
          {d === 365 ? "1y" : `${d}d`}
        </button>
      ))}
    </div>
  );
}

function TotalsRow({ totals }: { totals: import("../lib/api").ConnieTotals }) {
  // Resolution mix breakdown — answers "is the retry actually working?"
  const total = totals.violations;
  const succeededPct = total > 0 ? Math.round((totals.retrySucceeded / total) * 100) : 0;
  const fellThroughPct = total > 0 ? Math.round((totals.retryFailedThenSanitized / total) * 100) : 0;
  const noRetryPct = total > 0 ? Math.round((totals.sanitizedNoRetry / total) * 100) : 0;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Tile label="Violations" value={totals.violations.toLocaleString()} />
      <Tile label="Distinct patterns" value={totals.distinctPatterns.toLocaleString()} />
      <Tile
        label="Retry worked"
        value={total > 0 ? `${succeededPct}%` : "—"}
        sub={total > 0 ? `${totals.retrySucceeded} of ${total}` : undefined}
        tone={total === 0 ? "muted" : succeededPct >= 60 ? "good" : "warn"}
      />
      <Tile
        label="Sanitizer-only"
        value={total > 0 ? `${fellThroughPct + noRetryPct}%` : "—"}
        sub={
          total > 0
            ? `${totals.retryFailedThenSanitized} retried+sanitized · ${totals.sanitizedNoRetry} no-retry`
            : undefined
        }
        tone={total === 0 ? "muted" : fellThroughPct + noRetryPct >= 50 ? "warn" : undefined}
      />
    </div>
  );
}

function Tile({ label, value, sub, tone }: {
  label: string; value: string; sub?: string; tone?: "good" | "warn" | "muted";
}) {
  const cls =
    tone === "good" ? "bg-green-50 border-green-200 text-green-900"
    : tone === "warn" ? "bg-amber-50 border-amber-200 text-amber-900"
    : tone === "muted" ? "bg-ink-50 border-ink-200 text-ink-500"
    : "bg-white border-ink-200 text-ink-900";
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <div className="text-3xl font-display font-bold tabular-nums">{value}</div>
      <div className="text-xs uppercase tracking-wider opacity-70 mt-1">{label}</div>
      {sub && <div className="text-xs mt-1.5 opacity-80">{sub}</div>}
    </div>
  );
}

function EmptyState({ days }: { days: number }) {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-10 mt-6 text-center">
      <p className="text-sm text-ink-700 font-medium">No violations in the last {days === 365 ? "year" : `${days} days`}.</p>
      <p className="text-xs text-ink-500 mt-2 max-w-md mx-auto">
        Either nothing has been generated recently, or every generation passed audit on the first attempt.
        Connie's data fills in over time as you run <code className="bg-ink-100 px-1 rounded">rankforge generate</code>.
      </p>
    </div>
  );
}

function PatternsTable({ rows }: { rows: ConniePatternRow[] }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable(
    rows,
    {
      pattern: (r) => r.patternSource,
      count: (r) => r.count,
      distinct: (r) => r.distinctMatches,
      lastSeen: (r) => r.lastSeen,
    },
    { key: "count", dir: "desc" },
  );

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <h2 className="text-sm font-display font-bold text-ink-900 mb-3">Top recurring patterns</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider border-b border-ink-100">
              <SortHeader label="Pattern" sortKey="pattern" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-left" />
              <SortHeader label="Count" sortKey="count" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
              <SortHeader label="Distinct" sortKey="distinct" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" title="Number of distinct matched phrases — high distinct/count ratio means the AI is creative within the rule" />
              <SortHeader label="Last seen" sortKey="lastSeen" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((p) => (
              <tr key={p.patternSource} className="border-b border-ink-50 align-top">
                <td className="py-2.5">
                  <div className="text-ink-900 font-medium" title={p.reason}>{p.reason || "(no reason)"}</div>
                  <div className="text-xs text-ink-500 font-mono mt-0.5 truncate max-w-md" title={p.patternSource}>
                    /{p.patternSource}/
                  </div>
                  {p.exampleMatches.length > 0 && (
                    <div className="text-xs text-ink-600 mt-1.5 space-y-0.5">
                      {p.exampleMatches.slice(0, 3).map((m, i) => (
                        <div key={i} className="truncate max-w-md">
                          <span className="text-ink-400">e.g.</span> <span className="bg-amber-50 px-1 rounded">“{m}”</span>
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-2.5 text-right tabular-nums font-semibold">{p.count}</td>
                <td className="py-2.5 text-right tabular-nums text-ink-700">{p.distinctMatches}</td>
                <td className="py-2.5 text-right text-xs text-ink-500">{relTime(p.lastSeen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PageTypeBreakdown({ rows, total }: { rows: import("../lib/api").ConniePageTypeRow[]; total: number }) {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <h2 className="text-sm font-display font-bold text-ink-900 mb-3">By page type</h2>
      {rows.length === 0 ? (
        <p className="text-xs text-ink-500">No data.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const pct = total > 0 ? (r.count / total) * 100 : 0;
            return (
              <li key={r.pageType}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-ink-700 truncate" title={r.pageType}>{r.pageType}</span>
                  <span className="tabular-nums text-ink-900 font-semibold">{r.count}</span>
                </div>
                <div className="h-1.5 bg-ink-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-amber-500"
                    style={{ width: `${pct.toFixed(1)}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RecentFeed({ rows }: { rows: ConnieRecentRow[] }) {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <h2 className="text-sm font-display font-bold text-ink-900 mb-3">Recent violations</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[800px]">
          <thead>
            <tr className="text-left uppercase tracking-wider text-ink-500 border-b border-ink-100">
              <th className="pb-2">When</th>
              <th className="pb-2">Site</th>
              <th className="pb-2">Page</th>
              <th className="pb-2">Matched</th>
              <th className="pb-2 text-right">Attempt</th>
              <th className="pb-2">Resolution</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-ink-50">
                <td className="py-1.5 text-ink-500 whitespace-nowrap">{relTime(r.occurredAt)}</td>
                <td className="py-1.5 text-ink-700 truncate max-w-[140px]">{r.domain ?? <span className="text-ink-400">(none)</span>}</td>
                <td className="py-1.5 text-ink-700 font-mono truncate max-w-[160px]" title={r.pageType}>{r.pageType}</td>
                <td className="py-1.5 truncate max-w-[260px]">
                  <span className="bg-amber-50 px-1 rounded" title={r.matchedText}>“{r.matchedText}”</span>
                </td>
                <td className="py-1.5 text-right tabular-nums">{r.attemptNumber}</td>
                <td className="py-1.5">
                  <ResolutionPill resolution={r.resolution} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResolutionPill({ resolution }: { resolution: ConnieRecentRow["resolution"] }) {
  if (resolution === "retry_succeeded") {
    return <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-100 text-green-800">retry ok</span>;
  }
  if (resolution === "retry_failed_then_sanitized") {
    return <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">sanitized after retry</span>;
  }
  if (resolution === "sanitized_no_retry") {
    return <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-ink-200 text-ink-700">sanitized only</span>;
  }
  return <span className="text-[10px] font-mono text-ink-500">{resolution}</span>;
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms) || ms < 0) return "just now";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
